import { useEffect, useRef, useState } from 'react'

import { useFloorPlan } from '../hooks/useFloorPlan'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useSelector } from 'react-redux'

import { Box, Flex } from '@chakra-ui/react'

import {
  AlarmIcon,
  CameraIcon,
  CheckCircleIcon,
  DoorIcon,
  EditIcon,
  GuardsIcon,
  TrashIcon,
  fontFamilies,
} from '@hivewatch/dark'

import { DeviceType } from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { selectFacilityDrawerOpen } from '@/redux/ui/uiSlice'
import { FacilityDrawer } from '@/redux/ui/uiSlice.types'
import { mixpanel } from '@/utils/analytics'

import { DarkDeviceIconSizeSlider } from '../dark/DarkDeviceIconSizeSlider'
import { useFloorChart, useFloorPosition } from '../hooks'
import { ConnectionI, DeviceI, Size } from '../types/types'
import { convertCoordinatesDOMtoSVG } from '../utils/chart'
import {
  mapCheckpointData,
  mapConnectionsData,
  mapDevicesData,
} from '../utils/floorMappedData'
import {
  FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS,
  FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE,
  FloorPlanColorScheme,
  FloorPlanVisualVariant,
  getFloorPlanVisualColors,
} from '../utils/floorPlanVisuals'
import { DeviceIconSizeSlider } from './DeviceIconSizeSlider'

interface FloorChartContainerIProps {
  showBinIcon: boolean
  chartSize: number[] | Size
  chartInsets: {
    left: number
    top: number
    right: number
    bottom: number
  }
  imageUrl: string
  imageSize: Size
  devices: DeviceI[]
  connections: any[]
  isGuardTour?: boolean
  visualVariant?: FloorPlanVisualVariant
  colorScheme?: FloorPlanColorScheme
}

// Values found through trial & error and looking at the UI and seeing what felt reasonable
const MIN_FLOOR_PLAN_ICON_SIZE = 7.5
const MAX_FLOOR_PLAN_ICON_SIZE = 75
const REBRAND_TRASH_ICON_SIZE = 16
const REBRAND_TRASH_ICON_CENTER_Y = -14
const REBRAND_TRASH_TEXT_BASELINE_Y = 7

export const FloorChartContainer = ({
  showBinIcon,
  chartSize,
  chartInsets,
  imageUrl,
  imageSize,
  devices,
  connections,
  isGuardTour,
  visualVariant = 'legacy',
  colorScheme = 'dark',
}: FloorChartContainerIProps) => {
  const me = useSelector(selectMe)
  const svgRef = useRef(null)
  const mapImageRef = useRef(null)
  const mapGroupRef = useRef(null)

  const [mappedDevices, setMappedDevices] = useState<DeviceI[]>(null)
  const [mappedCheckpointDevices, setMappedCheckpointDevices] =
    useState<DeviceI[]>(null)
  const [mappedConnections, setMappedConnections] =
    useState<ConnectionI[]>(null)
  const [isRendered, setRendered] = useState(false)
  const [isZoomed, setZoomed] = useState(false)
  const [isTrashHovered, setIsTrashHovered] = useState(false)
  const trashHoverFrameRef = useRef<number | null>(null)

  const drawerOpen: FacilityDrawer = useSelector(selectFacilityDrawerOpen)

  const isFacilitiesRebrandEnabled = visualVariant === 'rebrand'
  const floorPlanColors = getFloorPlanVisualColors(colorScheme)

  const shouldShowIconResizer = !(
    drawerOpen === 'checkpoints' || drawerOpen === 'tour-plans'
  )

  const {
    validateDevicePosition,
    validateMapPosition,
    isDeviceInTrash,
    binPosition,
    rebrandBinPosition,
    validateRebrandDevicePosition,
  } = useFloorPosition(chartSize as Size, chartInsets, imageSize)

  const validatePosition = isFacilitiesRebrandEnabled
    ? validateRebrandDevicePosition
    : validateDevicePosition

  const [chartWidth, chartHeight] = chartSize
  const rebrandBinCenter = {
    x: rebrandBinPosition.x + rebrandBinPosition.width / 2,
    y: rebrandBinPosition.y + rebrandBinPosition.height / 2,
  }
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()
  const {
    floorId,
    checkpointDevices,
    highlightedDeviceIds,
    tourStopDeviceIds,
    floorPlanIconSize,
    onIconResize,
    onFloorPlanContainerClick,
    onDeviceClicked,
    onDeviceDragged,
    onDeviceDragEnd,
    onDeviceHover,
    onDeviceHoverOut,
    forceCloseHoverCards,
    onMoveCheckPointClicked,
    onConnectionButtonClicked,
    onRemoveCheckpoint,
    handleDeviceDropped,
  } = useFloorPlan()

  const onDrop = (e) => {
    e.stopPropagation()
    const { x, y } = convertCoordinatesDOMtoSVG(
      svgRef.current,
      mapGroupRef.current,
      e.clientX,
      e.clientY
    )
    const isValid = validateMapPosition(x, y)
    handleDeviceDropped([x, y], isValid, imageSize)
  }

  const refreshDevices = () => {
    const devicesData = mapDevicesData(devices)
    const connectionsData = mapConnectionsData(connections)
    const checkpointData = mapCheckpointData(checkpointDevices, floorId)
    setMappedDevices(devicesData)
    setMappedConnections(connectionsData)
    setMappedCheckpointDevices(checkpointData)
  }

  const { render, centerZoom } = useFloorChart({
    floorPlanIconSize: floorPlanIconSize,
    devices: mappedDevices,
    checkpoints: mappedCheckpointDevices,
    connections: mappedConnections,
    interactionsEnabled: true,
    onDeviceUpdated: (d: DeviceI) => onDeviceDragged(d, validatePosition(d)),
    onDeviceReleased: (d: DeviceI) => {
      onDeviceDragEnd(d, validatePosition(d))
      if (d.deviceType === DeviceType.Checkpoint) {
        mixpanel.track('Button Press: Map Checkpoint - End Moving', {
          checkpoint_id: d.checkpoint.id,
          facility_id: d.checkpoint.facility.id,
          floor_id: d.checkpoint.floor.id,
          role: me.roleName,
        })
      }
    },
    onDeviceClicked: onDeviceClicked,
    onDeviceHover: onDeviceHover,
    onDeviceHoverOut: onDeviceHoverOut,
    onForceCloseHoverCards: forceCloseHoverCards,
    onConnectionButtonClicked: onConnectionButtonClicked,
    onMoveCheckpoint: (d: DeviceI) => {
      onMoveCheckPointClicked(d.id)
      if (!d.editCheckpointPos) {
        mixpanel.track('Button Press: Map Checkpoint - Move Placement', {
          checkpoint_id: d.checkpoint.id,
          facility_id: d.checkpoint.facility.id,
          floor_id: d.checkpoint.floor.id,
          role: me.roleName,
        })
      }
    },
    onRemoveCheckpoint: onRemoveCheckpoint,
    highlightedDeviceIds: highlightedDeviceIds,
    tourStopDeviceIds,
    visualVariant,
    colorScheme,
    onTrashHoverChange: (device: DeviceI | null) => {
      if (trashHoverFrameRef.current !== null) {
        window.cancelAnimationFrame(trashHoverFrameRef.current)
        trashHoverFrameRef.current = null
      }

      if (!device) {
        setIsTrashHovered(false)
        return
      }

      trashHoverFrameRef.current = window.requestAnimationFrame(() => {
        trashHoverFrameRef.current = null
        setIsTrashHovered(isDeviceInTrash(device))
      })
    },
  })

  useEffect(() => {
    return () => {
      if (trashHoverFrameRef.current !== null) {
        window.cancelAnimationFrame(trashHoverFrameRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (svgRef.current && !isRendered) {
      render(svgRef.current, mapImageRef.current)
      setRendered(true)
    }
  }, [svgRef])

  useEffect(() => {
    if (isRendered && imageSize) {
      centerZoom(svgRef.current, mapImageRef.current)
      setZoomed(true)
      refreshDevices()
    }
  }, [svgRef, imageSize, isRendered])

  useEffect(() => {
    if (!isRendered || !devices || !isZoomed) return
    refreshDevices()
  }, [devices, connections, imageSize, isRendered, isZoomed])

  return (
    <Box width={chartWidth}>
      <Flex
        onClick={onFloorPlanContainerClick}
        onDragOver={(e) => {
          e.preventDefault()
        }}
        onDrop={onDrop}
        width='100%'
      >
        <svg height={chartHeight} ref={svgRef} width={chartWidth}>
          <defs>
            <linearGradient id='device-shape-gradient' x2='0.35' y2='1'>
              <stop offset='0%' stopColor='var(--color-stop)' />
              <stop offset='30%' stopColor='var(--color-stop)' />
              <stop offset='100%' stopColor='var(--color-bot)' />
            </linearGradient>
            <g id='floor-plan-camera-glyph'>
              <CameraIcon color='currentColor' size={20} />
            </g>
            <g id='floor-plan-door-glyph'>
              <DoorIcon color='currentColor' size={20} />
            </g>
            <g id='floor-plan-alarm-glyph'>
              <AlarmIcon color='currentColor' size={20} />
            </g>
            <g id='floor-plan-guard-glyph'>
              <GuardsIcon color='currentColor' size={20} />
            </g>
            <g id={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.remove.slice(1)}>
              <TrashIcon
                color='currentColor'
                size={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE}
              />
            </g>
            <g id={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.move.slice(1)}>
              <EditIcon
                color='currentColor'
                size={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE}
              />
            </g>
            <g id={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS.endMoving.slice(1)}>
              <CheckCircleIcon
                color='currentColor'
                size={FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE}
              />
            </g>
          </defs>
          <g className='map-group' ref={mapGroupRef}>
            <image
              className='map'
              height={imageSize ? imageSize[1] : 0}
              href={imageUrl}
              ref={mapImageRef}
              width={imageSize ? imageSize[0] : 0}
              x={0}
              y={0}
            />
            {!isGuardTour ? (
              <>
                <g className='camera-tools-group'></g>
                <g className={`connections-group ${isEditMode && 'editOn'}`} />
                <g className={`camera-group ${isEditMode && 'editOn'}`} />
                <g className={`door-group ${isEditMode && 'editOn'}`} />
                <g className={`alarm-group ${isEditMode && 'editOn'}`} />
              </>
            ) : (
              <g className='checkpoint-group' />
            )}
          </g>
          {showBinIcon && !isFacilitiesRebrandEnabled && (
            <image
              height={binPosition.height}
              href='FloorTrash.png'
              width={binPosition.width}
              x={binPosition.x}
              y={binPosition.y}
            />
          )}
          {showBinIcon && isFacilitiesRebrandEnabled && (
            <g
              data-state={isTrashHovered ? 'active' : 'idle'}
              opacity={isTrashHovered ? 1 : 0.4}
              pointerEvents='none'
            >
              <rect
                fill={floorPlanColors.deleteZoneActive}
                fillOpacity={isTrashHovered ? 0.15 : 0}
                height={rebrandBinPosition.height}
                rx={6}
                stroke={
                  isTrashHovered
                    ? floorPlanColors.deleteZoneActive
                    : floorPlanColors.deleteZoneIdle
                }
                strokeDasharray={isTrashHovered ? undefined : '4 4'}
                strokeWidth={1.5}
                width={rebrandBinPosition.width}
                x={rebrandBinPosition.x}
                y={rebrandBinPosition.y}
              />
              <g
                transform={`translate(${rebrandBinCenter.x}, ${rebrandBinCenter.y})`}
              >
                <image
                  height={REBRAND_TRASH_ICON_SIZE}
                  href={
                    colorScheme === 'light'
                      ? '/floor-plan/rebrand/trash-light.svg'
                      : '/floor-plan/rebrand/trash.svg'
                  }
                  width={REBRAND_TRASH_ICON_SIZE}
                  x={-REBRAND_TRASH_ICON_SIZE / 2}
                  y={REBRAND_TRASH_ICON_CENTER_Y - REBRAND_TRASH_ICON_SIZE / 2}
                />
                <text
                  fill={floorPlanColors.deleteZoneActive}
                  fontFamily={fontFamilies.soehneKraftig}
                  fontSize={12}
                  fontWeight={500}
                  textAnchor='middle'
                  x={0}
                  y={REBRAND_TRASH_TEXT_BASELINE_Y}
                >
                  <tspan x={0}>Drag device here</tspan>
                  <tspan dy={14} x={0}>
                    to remove
                  </tspan>
                </text>
              </g>
            </g>
          )}
        </svg>
      </Flex>

      {shouldShowIconResizer && (
        <Box bottom='5' left='5' position='absolute' zIndex={10}>
          {isFacilitiesRebrandEnabled ? (
            <DarkDeviceIconSizeSlider
              max={MAX_FLOOR_PLAN_ICON_SIZE}
              min={MIN_FLOOR_PLAN_ICON_SIZE}
              onChange={onIconResize}
              value={floorPlanIconSize}
            />
          ) : (
            <DeviceIconSizeSlider
              max={MAX_FLOOR_PLAN_ICON_SIZE}
              min={MIN_FLOOR_PLAN_ICON_SIZE}
              onChange={onIconResize}
              value={floorPlanIconSize}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
